// Vet rotation editor: a collapsible row per vet, with a tab per rota (past ones
// hidden by default). Each rota applies between its start and optional end date, so a
// change can be scheduled by ending the current rota and starting a new one. Each tab
// owns its edit state, so editing or saving one rota never disturbs another.
import { addDays, format, nextMonday, parseISO } from 'date-fns'
import { ChevronRight, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  blankWeekGrid,
  type ClinicId,
  DAY_KEYS,
  type DayKey,
  formatName,
  getClinic,
  getRotationWeekStart,
  getWeekIndex,
  type RegionId,
  type RotationCell,
  type RotationGrid,
  requireRegion,
  type ScheduleTemplate,
  type StaffMember,
  type StaffRotationEntry,
  toWallClockDate,
} from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { DatePicker } from '@/frontend/components/date-picker'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/frontend/components/ui/collapsible'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import { Popover, PopoverContent, PopoverTrigger } from '@/frontend/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/frontend/components/ui/tabs'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useRegionClinics } from '@/frontend/features/region/use-region-clinics'
import { templateLabel, templateOptions } from '@/frontend/features/schedules/template-options'
import { useScheduleTemplates } from '@/frontend/features/schedules/use-schedule-templates'
import {
  useCreateStaffRotation,
  useDeleteStaffRotation,
  useListStaffRotations,
  useUpdateStaffRotation,
} from '@/frontend/features/schedules/use-staff-rotations'

/**
 * Column order for the grid: Monday-first (Sunday last) so each row reads
 * week-start → week-end, matching the Mon–Sun rotation week. The canonical
 * DAY_KEYS is Sunday-first; this only reorders the rendered columns — the grid
 * data is keyed by day name, so order is purely cosmetic.
 */
const GRID_DAYS: DayKey[] = [...DAY_KEYS.slice(1), DAY_KEYS[0]]

/** Three-letter day labels for the compact header row. */
const DAY_LABELS: Record<DayKey, string> = {
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
}

const MS_PER_WEEK = 7 * 86_400_000
const MS_PER_DAY = 86_400_000

/** Tab value for the unsaved new rota. */
const NEW_ROTA = 'new'

interface CycleWeek {
  /** 1–4. */
  week: number
  /** Monday (start) and Sunday (end) of this week's occurrence in the cycle. */
  start: Date
  end: Date
  /** Whether `ref` falls in this week. */
  isCurrent: boolean
}

/**
 * Returns the Mon–Sun date range of each of the 4 rotation weeks for the cycle
 * containing `ref`, flagging the week `ref` falls in. Mirrors getWeekIndex's
 * Monday-anchored arithmetic so the highlighted week matches what getWorkingStaff
 * resolves. Dates are UTC-midnight stand-ins for the wall-clock day (format in UTC).
 */
function rotationCycleWeeks(ref: Date, timezone: string): CycleWeek[] {
  // Derive both the index and the week's Monday from the shared helpers so the
  // grid's dates + highlighted week can't drift from what the backend
  // (getWorkingStaff) resolves.
  const currentIndex = getWeekIndex(ref, timezone)
  const currentWeekMonday = getRotationWeekStart(ref, timezone).getTime()
  const cycleWeek1Monday = currentWeekMonday - currentIndex * MS_PER_WEEK
  return [0, 1, 2, 3].map((i) => ({
    week: i + 1,
    start: new Date(cycleWeek1Monday + i * MS_PER_WEEK),
    end: new Date(cycleWeek1Monday + i * MS_PER_WEEK + 6 * MS_PER_DAY),
    isCurrent: i === currentIndex,
  }))
}

/** Formats a UTC-midnight date as e.g. "29 Jun". */
function formatDay(date: Date): string {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

type RotaDates = Pick<StaffRotationEntry, 'startDate' | 'endDate'>
type RotaStatus = 'past' | 'current' | 'upcoming'

const toDateKey = (date: Date) => format(date, 'yyyy-MM-dd')
const shiftDay = (date: string, days: number) => toDateKey(addDays(parseISO(date), days))
const formatRotaDate = (date: string) => format(parseISO(date), 'd MMM yyyy')

function formatRange({ startDate, endDate }: RotaDates): string {
  return endDate
    ? `${formatRotaDate(startDate)} – ${formatRotaDate(endDate)}`
    : `From ${formatRotaDate(startDate)}`
}

/** Today's date in the region whose rotas are shown. */
function rotaToday(region: RegionId): string {
  return toWallClockDate(new Date(), requireRegion(region).timezone)
}

function rotaStatus({ startDate, endDate }: RotaDates, today: string): RotaStatus {
  if (endDate !== null && endDate < today) return 'past'
  return startDate > today ? 'upcoming' : 'current'
}

function overlaps(a: RotaDates, b: RotaDates): boolean {
  return (
    (a.endDate === null || b.startDate <= a.endDate) &&
    (b.endDate === null || a.startDate <= b.endDate)
  )
}

/** The stretches, from today on, when a vet has no rota at all. `rotas` must be sorted. */
function rotaGaps(rotas: RotaDates[], today: string): RotaDates[] {
  const gaps: RotaDates[] = []
  rotas.forEach((rota, i) => {
    if (rota.endDate === null) return
    const next = rotas[i + 1]
    const gapStart = shiftDay(rota.endDate, 1)
    if (!next) gaps.push({ startDate: gapStart, endDate: null })
    else if (gapStart < next.startDate)
      gaps.push({ startDate: gapStart, endDate: shiftDay(next.startDate, -1) })
  })
  return gaps.filter((g) => g.endDate === null || g.endDate >= today)
}

function hasWorkingDay(grid: RotationGrid): boolean {
  return grid.some((week) => DAY_KEYS.some((day) => week[day] !== null))
}

const STATUS_BADGE = {
  past: { label: 'Past', variant: 'neutral' },
  current: { label: 'Current', variant: 'success' },
  upcoming: { label: 'Upcoming', variant: 'info' },
} as const

/** The minimal staff shape a rotation row needs (matches the GET response + StaffMember). */
type RotationStaff = Pick<StaffMember, 'id' | 'firstName' | 'lastName'>

export function StaffRotationGrid() {
  const { region } = useViewerRegion()
  const { data: rotations } = useListStaffRotations(region)
  const { data: templates = [] } = useScheduleTemplates()

  // Whether the "Add rotation" staff picker is showing, and the vets added this
  // session that don't have a rotation yet (kept until a refetch surfaces them).
  const [adding, setAdding] = useState(false)
  const [pendingVets, setPendingVets] = useState<StaffMember[]>([])

  // The API returns rotas sorted by start date, so each vet's list stays sorted.
  const byVet = new Map<string, { staff: RotationStaff; rotas: StaffRotationEntry[] }>()
  for (const rota of rotations ?? []) {
    const entry = byVet.get(rota.staff.id) ?? { staff: rota.staff, rotas: [] }
    entry.rotas.push(rota)
    byVet.set(rota.staff.id, entry)
  }
  const vets = [...byVet.values()].sort((a, b) =>
    formatName(a.staff).localeCompare(formatName(b.staff))
  )
  const pendingToShow = pendingVets.filter((v) => !byVet.has(v.id))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        {adding ? (
          <StaffSearch
            value={null}
            onChange={(staff) => {
              if (staff) setPendingVets((prev) => [staff, ...prev])
              setAdding(false)
            }}
            // Only offer staff who don't already have (or aren't pending) a rotation.
            filter={(s) => !byVet.has(s.id) && !pendingVets.some((p) => p.id === s.id)}
            placeholder="Select a vet to add"
            allowClear
            className="w-64"
          />
        ) : (
          <Button variant="outline" onClick={() => setAdding(true)}>
            <Plus className="size-4" />
            Add rotation
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {pendingToShow.map((staff) => (
          <VetRotas
            key={staff.id}
            staff={staff}
            region={region}
            rotas={[]}
            templates={templates}
            defaultOpen
            onDiscardNew={() => setPendingVets((prev) => prev.filter((v) => v.id !== staff.id))}
          />
        ))}

        {vets.map(({ staff, rotas }) => (
          <VetRotas
            key={staff.id}
            staff={staff}
            region={region}
            rotas={rotas}
            templates={templates}
          />
        ))}

        {vets.length === 0 && pendingToShow.length === 0 && (
          <p className="text-muted-foreground">
            No rotations yet. Use “Add rotation” to set one up.
          </p>
        )}
      </div>
    </div>
  )
}

interface VetRotasProps {
  staff: RotationStaff
  region: RegionId
  /** Sorted by start date. */
  rotas: StaffRotationEntry[]
  templates: ScheduleTemplate[]
  defaultOpen?: boolean
  /** Called when a vet with no saved rota discards their draft. */
  onDiscardNew?: () => void
}

/** A vet's collapsible row: one tab per rota, plus an unsaved new rota when drafting. */
function VetRotas({ staff, rotas, region, templates, defaultOpen, onDiscardNew }: VetRotasProps) {
  const today = rotaToday(region)
  const current = rotas.find((r) => rotaStatus(r, today) === 'current')
  const firstUpcoming = rotas.find((r) => rotaStatus(r, today) === 'upcoming')

  const [showPast, setShowPast] = useState(false)
  const [drafting, setDrafting] = useState(rotas.length === 0)
  const [selected, setSelected] = useState(current?.id ?? firstUpcoming?.id ?? NEW_ROTA)

  const visible = rotas.filter((r) => showPast || rotaStatus(r, today) !== 'past')
  const hasPast = rotas.some((r) => rotaStatus(r, today) === 'past')
  const gaps = rotaGaps(rotas, today)

  // New rotas start as a copy of the rota in force, since most changes are tweaks to it.
  const newRotaSeed: StaffRotationEntry['grid'] =
    (current ?? rotas.at(-1))?.grid ?? blankWeekGrid<RotationCell>()

  const discardDraft = () => {
    setDrafting(false)
    if (rotas.length === 0) onDiscardNew?.()
    else setSelected(current?.id ?? firstUpcoming?.id ?? rotas.at(-1)!.id)
  }

  return (
    <Collapsible defaultOpen={defaultOpen} className="rounded-md border">
      <div className="flex items-center gap-2 px-3 py-2">
        <CollapsibleTrigger className="group flex items-center gap-2 font-medium">
          <ChevronRight className="size-4 text-muted-foreground transition-transform group-aria-expanded:rotate-90" />
          {formatName(staff)}
        </CollapsibleTrigger>
        <span className="text-sm text-muted-foreground">
          {current ? formatRange(current) : 'No current rota'}
          {firstUpcoming && ` · Next: ${formatRange(firstUpcoming)}`}
        </span>
      </div>
      {/* keepMounted so collapsing a vet's row doesn't discard their unsaved drafts. */}
      <CollapsibleContent keepMounted className="flex flex-col gap-3 px-3 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={selected} onValueChange={(value) => setSelected(value as string)}>
            <TabsList>
              {visible.map((rota) => {
                const badge = STATUS_BADGE[rotaStatus(rota, today)]
                return (
                  <TabsTrigger key={rota.id} value={rota.id}>
                    {formatRange(rota)}
                    <Badge variant={badge.variant} size="sm">
                      {badge.label}
                    </Badge>
                  </TabsTrigger>
                )
              })}
              {drafting && <TabsTrigger value={NEW_ROTA}>New rota</TabsTrigger>}
            </TabsList>
          </Tabs>
          {!drafting && (
            <Button
              variant="outline"
              onClick={() => {
                setDrafting(true)
                setSelected(NEW_ROTA)
              }}
            >
              <Plus className="size-4" />
              New rota
            </Button>
          )}
          {hasPast && (
            <Button variant="ghost" onClick={() => setShowPast((v) => !v)}>
              {showPast ? 'Hide past' : 'Show past'}
            </Button>
          )}
        </div>

        {gaps.map((gap) => (
          <p key={gap.startDate} className="text-sm text-muted-foreground">
            Not on rota{' '}
            {gap.endDate
              ? `${formatRotaDate(gap.startDate)} – ${formatRotaDate(gap.endDate)}`
              : `from ${formatRotaDate(gap.startDate)}`}
          </p>
        ))}

        {/* Every editor stays mounted and inactive tabs are hidden, so switching tabs to
            compare rotas keeps each one's unsaved edits. */}
        {drafting && (
          <div hidden={selected !== NEW_ROTA}>
            <RotaEditor
              staff={staff}
              region={region}
              initial={{
                startDate: toDateKey(nextMonday(parseISO(today))),
                endDate: null,
                grid: newRotaSeed,
              }}
              others={rotas}
              templates={templates}
              onSaved={(id) => {
                setDrafting(false)
                setSelected(id)
              }}
              onDiscard={discardDraft}
            />
          </div>
        )}
        {rotas.map((rota) => (
          <div key={`${rota.id}:${rota.startDate}:${rota.endDate}`} hidden={rota.id !== selected}>
            <RotaEditor
              staff={staff}
              region={region}
              initial={rota}
              editing={rota.id}
              started={rotaStatus(rota, today) !== 'upcoming'}
              others={rotas.filter((r) => r.id !== rota.id)}
              templates={templates}
              onSaved={setSelected}
              onDiscard={() =>
                setSelected(
                  rotas.find((r) => r !== rota && rotaStatus(r, today) !== 'past')?.id ?? NEW_ROTA
                )
              }
            />
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

interface RotaEditorProps {
  staff: RotationStaff
  region: RegionId
  initial: Pick<StaffRotationEntry, 'startDate' | 'endDate' | 'grid'>
  /** Id of the saved rota being edited; omitted for a new rota. */
  editing?: string
  /** Deleting a rota that has started also clears the past days it covered, so the confirm says so. */
  started?: boolean
  /** The vet's other rotas, checked for overlaps. */
  others: StaffRotationEntry[]
  templates: ScheduleTemplate[]
  /** Called with the saved rota's id. */
  onSaved: (id: string) => void
  /** Called when a new rota is discarded or a saved one deleted. */
  onDiscard: () => void
}

/** One rota's dates and 4-week grid, with its own edit state, Save and Delete. */
function RotaEditor({
  staff,
  region,
  initial,
  editing,
  started,
  others,
  templates,
  onSaved,
  onDiscard,
}: RotaEditorProps) {
  // Seed local edit state once from props; the editor owns it from then on so a
  // sibling save / background refetch never clobbers in-progress edits.
  const [startDate, setStartDate] = useState(initial.startDate)
  const [endDate, setEndDate] = useState(initial.endDate)
  const [grid, setGrid] = useState<RotationGrid>(() => structuredClone(initial.grid))
  const [dirty, setDirty] = useState(!editing)
  // An earlier rota to end the day before this one starts, applied only when this is saved.
  const [endingId, setEndingId] = useState<string | null>(null)
  const create = useCreateStaffRotation()
  const update = useUpdateStaffRotation()
  const remove = useDeleteStaffRotation()

  const today = rotaToday(region)
  const dates = { startDate, endDate }
  // Re-derived from the current start date, and dropped if the dates no longer call for it.
  const ending = others.find(
    (o) => o.id === endingId && o.startDate < startDate && overlaps(o, dates)
  )
  const endingOn = shiftDay(startDate, -1)
  const clash = others.find((o) => o.id !== ending?.id && overlaps(o, dates))
  const invalidRange = endDate !== null && endDate < startDate
  const empty = !hasWorkingDay(grid)
  const saving = create.isPending || update.isPending

  // Week labels follow the cycle the rota starts in once it's in the future, so the
  // planner can see which rota week it picks up on.
  const ref = startDate > today ? startDate : today
  const cycleWeeks = rotationCycleWeeks(
    new Date(`${ref}T12:00:00.000Z`),
    requireRegion(region).timezone
  )

  /** Immutably updates a single cell and marks the grid dirty. */
  const setCell = (weekIndex: number, day: DayKey, cell: RotationCell | null) => {
    setGrid((prev) => prev.map((week, i) => (i === weekIndex ? { ...week, [day]: cell } : week)))
    setDirty(true)
  }

  const onError = (err: Error) => toast.error(err.message)

  const onSave = async () => {
    try {
      if (ending) {
        await update.mutateAsync({
          id: ending.id,
          body: { startDate: ending.startDate, endDate: endingOn, grid: ending.grid },
        })
      }
    } catch (err) {
      onError(err as Error)
      return
    }

    try {
      if (editing) {
        await update.mutateAsync({ id: editing, body: { startDate, endDate, grid } })
        setDirty(false)
        setEndingId(null)
        onSaved(editing)
      } else {
        const created = await create.mutateAsync({
          staffId: staff.id,
          region,
          startDate,
          endDate,
          grid,
        })
        onSaved(created.id)
      }
    } catch (err) {
      onError(err as Error)
      if (!ending) return
      // Put the earlier rota back, so a failed save doesn't leave the vet without a rota.
      await update
        .mutateAsync({
          id: ending.id,
          body: { startDate: ending.startDate, endDate: ending.endDate, grid: ending.grid },
        })
        .catch(() =>
          toast.error(
            `Couldn't restore the rota ${formatRange(ending).replace(/^From/, 'from')}. Check its end date.`
          )
        )
    }
  }

  // An earlier rota is staged to end; for a later one, this draft's own end date moves.
  const fixClash = (other: StaffRotationEntry) => {
    if (other.startDate < startDate) setEndingId(other.id)
    else setEndDate(shiftDay(other.startDate, -1))
    setDirty(true)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-muted-foreground">Start</span>
          <DatePicker
            value={parseISO(startDate)}
            onChange={(date) => {
              if (!date) return
              setStartDate(toDateKey(date))
              setDirty(true)
            }}
            className="w-44"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-muted-foreground">End</span>
          <div className="flex items-center gap-1">
            <DatePicker
              value={endDate ? parseISO(endDate) : undefined}
              onChange={(date) => {
                setEndDate(date ? toDateKey(date) : null)
                setDirty(true)
              }}
              fromDate={parseISO(startDate)}
              placeholder="Ongoing"
              className="w-44"
            />
            {endDate && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Make ongoing"
                onClick={() => {
                  setEndDate(null)
                  setDirty(true)
                }}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {editing ? (
            <ConfirmButton
              variant="ghost"
              heading="Delete this rota?"
              description={
                started
                  ? `${formatName(staff)} will have no rota ${formatRange(initial).replace(/^From/, 'from')}, including the days it already covered. To stop it from now on, set an end date instead.`
                  : `${formatName(staff)} will have no rota ${formatRange(initial).replace(/^From/, 'from')}.`
              }
              confirmLabel="Delete"
              dialogColor="destructive"
              disabled={remove.isPending}
              onConfirm={() => remove.mutate(editing, { onSuccess: onDiscard, onError })}
            >
              Delete
            </ConfirmButton>
          ) : (
            <Button variant="ghost" onClick={onDiscard}>
              Discard
            </Button>
          )}
          <Button disabled={!dirty || saving || !!clash || invalidRange || empty} onClick={onSave}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {clash && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-warning-fg">
          Overlaps the rota {formatRange(clash).replace(/^From/, 'from')}.
          {clash.startDate !== startDate && (
            <Button variant="outline" onClick={() => fixClash(clash)}>
              {clash.startDate < startDate
                ? `End that rota on ${formatRotaDate(shiftDay(startDate, -1))}`
                : `End this rota on ${formatRotaDate(shiftDay(clash.startDate, -1))}`}
            </Button>
          )}
        </p>
      )}
      {ending && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          The rota {formatRange(ending).replace(/^From/, 'from')} will end on{' '}
          {formatRotaDate(endingOn)} when you save.
          <Button variant="ghost" onClick={() => setEndingId(null)}>
            Undo
          </Button>
        </p>
      )}
      {empty && <p className="text-sm text-muted-foreground">Add at least one working day.</p>}

      <table className="border-collapse">
        <thead>
          <tr>
            <th className="w-16 p-2 text-left font-medium text-muted-foreground" />
            {GRID_DAYS.map((day) => (
              <th key={day} className="p-2 text-center font-medium text-muted-foreground">
                {DAY_LABELS[day]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((week, i) => {
            const cw = cycleWeeks[i]
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: weeks are fixed positions in the cycle
              <tr key={i} className={cw.isCurrent ? 'bg-muted/50' : undefined}>
                <th className="p-2 text-left align-top whitespace-nowrap">
                  <div className="font-medium text-muted-foreground">
                    Week {i + 1}
                    {cw.isCurrent && (
                      <span className="ml-1 text-primary">
                        · {ref === today ? 'this week' : 'starts'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-normal text-muted-foreground">
                    {formatDay(cw.start)} – {formatDay(cw.end)}
                  </div>
                </th>
                {GRID_DAYS.map((day) => (
                  <td key={day} className="p-1 align-top">
                    <CellEditor
                      cell={week[day]}
                      templates={templates}
                      onChange={(cell) => setCell(i, day, cell)}
                    />
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

interface CellEditorProps {
  cell: RotationCell | null
  templates: ScheduleTemplate[]
  onChange: (cell: RotationCell | null) => void
}

/** Popover editor for a single rotation cell: clinic + template, or Off. */
function CellEditor({ cell, templates, onChange }: CellEditorProps) {
  const regionClinics = useRegionClinics()
  const templateItems = templateOptions(templates, cell?.templateId)
  const activeTemplates = templates.filter((t) => t.archivedAt === null)

  const clinicLabel = cell ? (getClinic(cell.clinicId)?.name ?? cell.clinicId) : null
  const cellTemplate = cell && templates.find((t) => t.id === cell.templateId)
  const cellTemplateLabel = cell
    ? cellTemplate
      ? templateLabel(cellTemplate)
      : cell.templateId
    : null

  // Picking a clinic keeps the current template (or defaults to the first).
  const onClinicChange = (clinicId: ClinicId | null) => {
    if (!clinicId) return
    const templateId = cell?.templateId ?? activeTemplates[0]?.id
    if (!templateId) return
    onChange({ clinicId, templateId })
  }

  // Picking a template keeps the current clinic (or defaults to the region's first).
  const onTemplateChange = (templateId: string | null) => {
    if (!templateId) return
    const clinicId = cell?.clinicId ?? regionClinics[0]?.id
    if (!clinicId) return
    onChange({ clinicId, templateId })
  }

  const label = cell ? `${clinicLabel} · ${cellTemplateLabel}` : 'Off'
  return (
    <Popover>
      <PopoverTrigger render={<Button variant="outline" className="w-32 justify-start truncate" />}>
        {label}
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-muted-foreground">Clinic</span>
          {/* allowClear stops it auto-picking a one-clinic region's clinic, which would turn an Off day on. */}
          <ClinicSearch
            value={cell?.clinicId ?? null}
            onChange={onClinicChange}
            placeholder="Select a clinic"
            allowClear
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-muted-foreground">Template</span>
          <Select
            items={templateItems}
            value={cell?.templateId ?? ''}
            onValueChange={onTemplateChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a template" />
            </SelectTrigger>
            <SelectContent>
              {templateItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="ghost" onClick={() => onChange(null)}>
          Set Off
        </Button>
      </PopoverContent>
    </Popover>
  )
}
